import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';

/**
 * ViewModel for CSV HITL Review
 * Unified single-step review flow
 */
export const useCsvReviewViewModel = (model) => {
  const [state, setState] = useState(model.getState());

  useEffect(() => {
    const unsubscribe = model.subscribe((newState) => {
      setState(newState);
    });
    setState(model.getState());
    return unsubscribe;
  }, [model]);

  const openReview = useCallback(async (datasetId, jobId) => {
    model.setDatasetId(datasetId);
    model.setJobId(jobId);
    model.setPhase('loading');
    model.setError(null);

    try {
      const response = await apiService.getCsvPendingReview(jobId);
      model.setReviewData(response.payload);
      model.setPhase('reviewing');
    } catch (error) {
      console.error('[CsvReviewVM] Error fetching pending review:', error);
      model.setError(error.message || 'Failed to load review data');
      model.setPhase('failed');
    }
  }, [model]);

  // Returns an outcome string so callers can branch without sniffing phase:
  //   'processing' (200) | 'conflict' (409) | 'incomplete' (422) | 'error'
  const submitReview = useCallback(async () => {
    const currentState = model.getState();
    model.setPhase('submitting');
    model.setError(null);
    model.setInvalidColumns([]);

    try {
      // Build the submit contract (PRD-893): one approved_definitions entry per table,
      // snake_name == table_name, fields cover every csv_column with a non-empty
      // description. pg_type is optional — include the known/suggested type.
      // source_column (PRD-949) is the immutable original CSV header — it passes back
      // unchanged so the backend can tie the (possibly renamed) field to its CSV data.
      const approved = currentState.editedDefinitions.map(def => ({
        name: def.name,
        snake_name: def.snake_name,
        description: def.description,
        fields: def.fields.map(f => {
          const field = { name: f.name, description: f.description };
          if (f.pg_type) field.pg_type = f.pg_type;
          field.source_column = f.source_column != null ? f.source_column : f.name;
          return field;
        }),
      }));

      const reviewData = {
        definitions: {
          approved_definitions: approved,
        },
      };

      await apiService.submitCsvReview(currentState.jobId, reviewData);
      model.setPhase('completed');
      return 'processing';
    } catch (error) {
      console.error('[CsvReviewVM] Error submitting review:', error);

      // 409 — job already processing/resumed (double-submit or stale modal). It moved
      // on; don't treat as an error, just close and let polling refresh status.
      if (error.status === 409) {
        model.setPhase('completed');
        return 'conflict';
      }

      // 422 — an incomplete approval slipped through. Highlight the listed columns and
      // keep the modal open; do not retry blindly.
      const detail = error.responseData && error.responseData.detail;
      if (error.status === 422 && detail && detail.error === 'incomplete_review') {
        model.setInvalidColumns(detail.columns || []);
        model.setError('Some columns still need a description. Please complete the highlighted fields.');
        model.setPhase('reviewing');
        return 'incomplete';
      }

      model.setError(error.message || 'Failed to submit review');
      model.setPhase('reviewing');
      return 'error';
    }
  }, [model]);

  const updateDefinition = useCallback((defIndex, field, value) => {
    model.updateDefinition(defIndex, field, value);
  }, [model]);

  const updateDefinitionField = useCallback((defIndex, fieldIndex, fieldKey, value) => {
    model.updateDefinitionField(defIndex, fieldIndex, fieldKey, value);
  }, [model]);

  const addField = useCallback((defIndex) => {
    model.addField(defIndex);
  }, [model]);

  const removeField = useCallback((defIndex, fieldIndex) => {
    model.removeField(defIndex, fieldIndex);
  }, [model]);

  const reset = useCallback(() => {
    model.reset();
  }, [model]);

  const getPhase = useCallback(() => {
    return model.getState().phase;
  }, [model]);

  return {
    ...state,
    openReview,
    submitReview,
    getPhase,
    updateDefinition,
    updateDefinitionField,
    addField,
    removeField,
    reset,
  };
};
